// Главная сайта (vydykhayflowers.ru в обычном браузере), макет «Сайт Выдыхай: заказ без Telegram».
// Заказ прямо на сайте без регистрации: телефон и почта. Telegram предлагаем как более удобный путь.

import { useEffect, useState, type CSSProperties } from 'react';
import { api, type AppConfig, type Design } from '../lib/api.ts';
import { rub } from '../lib/format.ts';
import { BouquetArt, Flower } from '../ui/kit.tsx';
import { track } from '../lib/events.ts';
import type { Screen } from '../lib/store.tsx';

const BOT = 'VydykhayFlowersBot';
const SELLER = {
  name: 'ИП Лосева Екатерина Александровна',
  inn: '504711870940',
  ogrnip: '326508100214812',
  email: 'order@vydykhayflowers.ru',
};

const ZONES: Array<{ key: 'zone1' | 'zone2' | 'zone3' | null; name: string; dot: CSSProperties }> = [
  { key: 'zone1', name: 'Центр, внутри Садового', dot: { background: 'var(--pink-line)' } },
  { key: 'zone2', name: 'От Садового до ТТК', dot: { background: 'var(--pink)', border: '1px solid var(--pink-line)' } },
  { key: 'zone3', name: 'От ТТК до МКАД', dot: { background: 'var(--butter-soft)', border: '1px solid var(--butter-line)' } },
  { key: null, name: 'За МКАД', dot: { background: 'var(--sand)', border: '1px dashed var(--line-2)' } },
];

const Check = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true" style={{ flex: 'none', marginTop: 1 }}>
    <path d="M5 12l5 5L20 7" />
  </svg>
);

export function Landing({ onStart }: { onStart: (s: Screen) => void }) {
  const [designs, setDesigns] = useState<Design[]>([]);
  const [cfg, setCfg] = useState<AppConfig | null>(null);
  useEffect(() => {
    api.catalog().then((c) => setDesigns(c.designs)).catch(() => {});
    api.config().then(setCfg).catch(() => {});
  }, []);
  // Метка источника (?r=blog_anya или utm_source) уходит в бота: t.me/бот?start=r_blog_anya
  const q = new URLSearchParams(location.search);
  const ref = (q.get('r') ?? q.get('utm_source') ?? '').replace(/[^a-zA-Z0-9_-]/g, '').slice(0, 40);
  const tg = `https://t.me/${BOT}${ref ? `?start=r_${ref}` : ''}`;
  const from = (d: Design) => Math.min(...d.sizes.filter((s) => s.available).map((s) => s.price));
  const shown = designs.filter((d) => d.sizes.some((s) => s.available)).slice(0, 4);
  const hero = designs.find((d) => d.photoUrl) ?? designs[0];
  const start = (button: string, s: Screen = { name: 'main' }) => {
    track('site_start', { button });
    onStart(s);
  };

  return (
    <div className="landing site">
      <header className="l-wrap l-head">
        <img src="/logo.png" alt="Выдыхай" style={{ width: 130, minWidth: 0, flexShrink: 1, height: 'auto', mixBlendMode: 'multiply' }} />
        <button type="button" className="btn light sm" style={{ minHeight: 44, fontSize: 14, flex: 'none', padding: '0 14px' }} onClick={() => start('my_orders', { name: 'myOrders' })}>
          Мои заказы
        </button>
      </header>

      <section className="l-wrap l-hero" style={{ marginTop: 28 }}>
        <div className="stack s14" style={{ maxWidth: 560 }}>
          <span className="s-chip">Цветочная мастерская в Москве</span>
          <h1 className="h1 big" style={{ fontSize: 'clamp(34px, 6vw, 52px)' }}>
            Выдыхай, букет подберём мы
          </h1>
          <p className="lead">Пара вопросов и несколько свайпов вместо бесконечного каталога. В конце три букета, которые подходят лучше всего.</p>
          <button type="button" className="btn" onClick={() => start('hero')}>
            Подобрать букет
          </button>
          <span className="small center">Без регистрации. Нужны только телефон и почта.</span>
          <div className="s-tg stack s14">
            <span className="h2" style={{ fontSize: 20 }}>
              В Telegram ещё удобнее
            </span>
            <span className="s-point">
              <Check />
              Фото букета и ответы живого флориста сразу в чате
            </span>
            <span className="s-point">
              <Check />
              Статус заказа всегда под рукой, без писем и ссылок
            </span>
            <span className="s-point">
              <Check />
              Запомним твой вкус и с каждым разом будем предлагать точнее
            </span>
            <a className="btn light" href={tg} onClick={() => track('site_to_telegram', { button: 'hero' })} target="_blank" rel="noopener" style={{ minHeight: 48 }}>
              Открыть в Telegram
            </a>
          </div>
        </div>
        <div className="s-photo">
          {hero ? <BouquetArt design={hero} height={300} /> : <Flower size={200} fill="#F7ACD6" center="#FCE3F0" />}
          <span className="s-photo-tag">Фото перед отправкой, всегда</span>
        </div>
      </section>

      <section className="l-wrap stack s14">
        <h2 className="h2" style={{ fontSize: 26 }}>
          Как это работает
        </h2>
        <ol className="l-steps">
          <li>
            <b>Для кого и когда</b>
            <span>Себе, в подарок или сюрприз от флориста. Дата и окно доставки.</span>
          </li>
          <li className="pink">
            <b>Несколько свайпов</b>
            <span>Форма, цвета, любимые цветы. Если вкус получателя неизвестен, расскажи о человеке.</span>
          </li>
          <li>
            <b>Три букета на выбор</b>
            <span>Лучшее совпадение и ещё два. Оплата картой или по СБП.</span>
          </li>
          <li className="pink">
            <b>Фото до отправки</b>
            <span>Покажем фото готового букета на странице заказа и пришлём на почту. Что-то не так, поправим до отправки.</span>
          </li>
        </ol>
      </section>

      {shown.length > 0 && (
        <section className="l-wrap stack s14">
          <div className="row" style={{ justifyContent: 'space-between', alignItems: 'baseline' }}>
            <h2 className="h2" style={{ fontSize: 26 }}>
              Букеты
            </h2>
            <button type="button" className="link" style={{ fontSize: 15, minHeight: 44 }} onClick={() => start('catalog', { name: 'catalog' })}>
              Весь каталог
            </button>
          </div>
          <div className="l-grid">
            {shown.map((d) => (
              <article key={d.id} className="pcard">
                <BouquetArt design={d} height={150} />
                <span className="meta">
                  <span className="name">{d.name}</span>
                  <span className="price" style={{ fontSize: 15 }}>
                    от {rub(from(d))}
                  </span>
                </span>
              </article>
            ))}
          </div>
        </section>
      )}

      <section className="l-wrap">
        <div className="s-delivery stack s14">
          <h2 className="h2">Доставка и оплата</h2>
          <div className="stack" style={{ gap: 0 }}>
            {ZONES.map((z, i) => (
              <div key={z.name} className="row" style={{ justifyContent: 'space-between', padding: '9px 0', borderBottom: i < ZONES.length - 1 ? '1px solid var(--line-2)' : 'none', fontSize: 15 }}>
                <span className="row" style={{ gap: 8 }}>
                  <span style={{ width: 14, height: 14, borderRadius: 7, flex: 'none', ...z.dot }} />
                  {z.name}
                </span>
                <span style={{ color: z.key ? 'var(--ink)' : 'var(--ink-2)', whiteSpace: 'nowrap' }}>{z.key ? (cfg?.delivery[z.key] != null ? rub(cfg.delivery[z.key]) : '…') : 'посчитает флорист'}</span>
              </div>
            ))}
          </div>
          <p className="p">Окна по 2 часа с 9:00 до 22:00. Букет собираем за час после оплаты и везём в переноске с водой.</p>
          <p className="p">Оплата картой или по СБП через Т-Банк. Чек придёт на почту.</p>
        </div>
      </section>

      <section className="l-wrap l-cols">
        <div className="card stack">
          <h2 className="h2">Замена и возврат</h2>
          <p className="p">Перед отправкой показываем фото букета, его можно попросить поправить. Если с цветами что-то не так при получении, напиши на странице заказа в течение 24 часов: заменим букет или вернём деньги.</p>
        </div>
      </section>

      <footer className="l-wrap l-foot">
        <div className="stack s6">
          <b>Документы</b>
          <a href="/docs/offer.html">Публичная оферта</a>
          <a href="/docs/delivery.html">Доставка, замена и возврат</a>
          <a href="/docs/privacy.html">Политика обработки персональных данных</a>
        </div>
        <div className="stack s6">
          <b>Контакты</b>
          <a href={`mailto:${SELLER.email}`}>{SELLER.email}</a>
          <a href={tg} onClick={() => track('site_to_telegram', { button: 'footer' })} target="_blank" rel="noopener">
            Telegram: @{BOT}
          </a>
          <span>Отвечаем ежедневно 8:00–22:00</span>
        </div>
        <div className="stack s6">
          <b>Продавец</b>
          <span>{SELLER.name}</span>
          <span>ИНН {SELLER.inn}</span>
          <span>ОГРНИП {SELLER.ogrnip}</span>
        </div>
      </footer>
    </div>
  );
}
